/**
 * A worked example for a tariff sheet, built from that sheet's own rates.
 *
 * The note explaining base rate plus excess is abstract; a concrete sum using
 * the customer's real numbers is what makes it land. Shared by the on-screen
 * table and the PDF so the two can never disagree.
 *
 * Mirrors RateCalculationService::calculateAdditionalCharge():
 *   total = base + (excess kg x multiplier x band rate)
 * where the multiplier is 2 for documents (charged per 0.5 kg) and 1 otherwise.
 *
 * Returns null when no zone has both a base rate and a weight band, since there
 * is nothing worth demonstrating in that case.
 */
export const buildTariffExample = (sheet) => {
    if (!sheet?.zones?.length) return null;

    const hasDiscount = Number(sheet.discount?.package_disc || 0) > 0;
    const rateOf = (obj, netKey, listKey) => Number(hasDiscount ? obj[netKey] : obj[listKey]);

    // Prefer a zone that can show the full calculation. Falling back to a
    // base-rate-only zone still beats showing nothing.
    const withBand = sheet.zones.find((z) => z.base_rate !== null && z.bands?.length > 0);
    const zone = withBand || sheet.zones.find((z) => z.base_rate !== null);
    if (!zone) return null;

    const defaultTo = Number(sheet.product.default_weight_to);
    const base = rateOf(zone, 'net_base_rate', 'base_rate');

    if (!withBand) {
        // No excess band: any shipment up to the ceiling is simply the base rate.
        const weight = Math.max(defaultTo, 1);
        return {
            zone: zone.zone,
            weight,
            base,
            excessKg: 0,
            total: base,
            withinBand: true,
        };
    }

    const band = zone.bands[0];
    const bandRate = rateOf(band, 'net_rate', 'rate');
    const multiplier = Number(sheet.product.excess_multiplier || 1);

    // Pick a weight a little past the default ceiling, kept inside the first
    // band so the arithmetic shown is the arithmetic that would actually apply.
    const weight = Math.min(defaultTo + 10, Number(band.weight_to));
    const excessKg = Number((weight - defaultTo).toFixed(2));
    const units = excessKg * multiplier;
    const excessCharge = Number((units * bandRate).toFixed(2));

    return {
        zone: zone.zone,
        weight,
        base,
        excessKg,
        multiplier,
        units,
        bandRate,
        bandRange: band.range,
        excessCharge,
        total: Number((base + excessCharge).toFixed(2)),
        withinBand: false,
    };
};

/** The example rendered as plain sentences, used by both the view and the PDF. */
export const tariffExampleLines = (example, currency = 'Rs.') => {
    if (!example) return [];

    const money = (v) => `${currency} ${Number(v).toFixed(2)}`;

    if (example.withinBand) {
        return [
            `Example: a ${example.weight} kg shipment to ${example.zone} costs ${money(example.base)}.`,
        ];
    }

    const unitNote = example.multiplier === 2
        ? `${example.excessKg} kg excess = ${example.units} units of 0.5 kg`
        : `${example.excessKg} kg excess`;

    return [
        `Example: a ${example.weight} kg shipment to ${example.zone}.`,
        `Base rate (up to ${example.weight - example.excessKg} kg): ${money(example.base)}`,
        `Excess (${unitNote} x ${money(example.bandRate)}): ${money(example.excessCharge)}`,
        `Total: ${money(example.total)}`,
    ];
};
